import React from 'react'
import { Routes, Route, Navigate, NavLink, useNavigate } from 'react-router-dom'
import { Users, Building2, LogOut, ChevronLeft } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import NotificationBell from '../../components/shared/NotificationBell'
import AdminClients from './pages/AdminClients'
import AdminClientDetail from './pages/AdminClientDetail'
import AdminCollaborateurs from './pages/AdminCollaborateurs'

const LOGO_WHITE = '/logo-white.png'

const NAV = [
  { to: '/admin/clients', icon: Building2, label: 'Clients' },
  { to: '/admin/collaborateurs', icon: Users, label: 'Collaborateurs C2' },
]

function Sidebar() {
  const { profile, signOut } = useAuth()
  const navigate = useNavigate()

  async function handleLogout() {
    await signOut()
    navigate('/login', { replace: true })
  }

  const canAccessC2 = ['master_admin', 'c2_admin', 'c2_gestionnaire'].includes(profile?.role)

  return (
    <aside
      className="w-[240px] min-h-screen flex flex-col shrink-0"
      style={{ background: '#1e1b4b' }}
      data-testid="admin-sidebar"
    >
      <div className="px-4 py-4 border-b border-white/10">
        <div className="flex items-center justify-between">
          <img src={LOGO_WHITE} alt="C2 Assurances" className="h-8 w-auto" />
          <NotificationBell />
        </div>
        <p className="text-xs text-indigo-300 mt-1.5 ml-0.5 font-semibold">Administration</p>
      </div>

      {canAccessC2 && (
        <div className="px-3 py-3 border-b border-white/10">
          <NavLink to="/c2/dashboard" className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-indigo-300 hover:bg-white/10 hover:text-white transition-colors">
            <ChevronLeft size={16} /> Retour au portail C2
          </NavLink>
        </div>
      )}

      <nav className="flex-1 px-3 py-4 space-y-0.5">
        {NAV.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={`nav-${label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                isActive ? 'bg-white/15 text-white font-semibold' : 'text-indigo-200 hover:bg-white/8 hover:text-white'
              }`
            }
          >
            <Icon size={16} strokeWidth={1.5} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="px-3 pb-4 space-y-0.5 border-t border-white/10 pt-3">
        <div className="px-3 py-2 mt-1">
          <div className="flex items-center gap-2.5 mb-2">
            <div className="w-7 h-7 rounded-full bg-indigo-500/30 flex items-center justify-center text-indigo-200 text-xs font-bold">
              {profile?.prenom?.[0]}{profile?.nom?.[0]}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-white truncate">{profile?.prenom} {profile?.nom}</p>
              <p className="text-xs text-indigo-300 truncate">{profile?.role === 'master_admin' ? 'Master Admin' : profile?.role}</p>
            </div>
          </div>
          <button onClick={handleLogout} data-testid="logout-button" className="flex items-center gap-2 text-xs text-indigo-300 hover:text-red-400 transition-colors">
            <LogOut size={13} /> Deconnexion
          </button>
        </div>
        <p className="text-center text-[10px] text-indigo-400/50 pt-2">Propriete de C2 Assurances</p>
      </div>
    </aside>
  )
}

export default function AdminApp() {
  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <main className="flex-1 overflow-auto">
        <Routes>
          <Route index element={<Navigate to="clients" replace />} />
          <Route path="clients" element={<AdminClients />} />
          <Route path="clients/:id" element={<AdminClientDetail />} />
          <Route path="collaborateurs" element={<AdminCollaborateurs />} />
          <Route path="*" element={<Navigate to="clients" replace />} />
        </Routes>
      </main>
    </div>
  )
}
